// #region Imports
import BadgeIcon from "@mui/icons-material/Badge";
import MessageIcon from "@mui/icons-material/Message";
import { styled, Theme } from "@mui/material";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query/react";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import Name from "_shared/names/name";
import TeamAvatar from "marketplace/_views/avatars/team-avatar";
import { useGetTeamByIdQuery } from "marketplace/teams/_api/marketplace-team-api";
import {
  getTeamById
} from "marketplace/teams/_store/marketplace-teams-redux-slice";
import ViewTeam from "marketplace/teams/_views/view-team";
import TeamFeeSchedules from "marketplace/teams/fee-schedules/_views/team-fee-schedules";
import ChatWindow from "messaging/forums/_views/components/forum-chat-window";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import { useGetChatForumForNetworkConnectionQuery } from "users/networking/connections/_api/user-network-connection-api";
import useNetworkConnectionStatus from "users/networking/connections/_hooks/use-network-connection-status";
import NetworkButton from "users/networking/connections/_views/components/network-button";
import TeamProfile from "../team-profile";
import Forum from "messaging/forums/forum";
import DirectProposalButton from "work/proposals/_views/_buttons/direct-proposal-button";
// #endregion

// #region Header Layout
/**
 * The header actions are a network button (which renders a button group), a message button wrapped
 * in a tooltip span, and the proposal buttons. Selecting them together is what keeps the row even.
 */
const HEADER_ACTIONS = "& > .MuiButton-root, & > .MuiButtonGroup-root, & > span";
/** Width, in theme spacing units, that every header action is laid out at. */
const HEADER_ACTION_WIDTH = 25;
/** Edge of the team avatar, in theme spacing units. */
const AVATAR_WIDTH = 15;
/** Gap between the header's columns and between its actions, in theme spacing units. */
const HEADER_GAP = 2;
/** Narrowest description column that still reads as a paragraph, in theme spacing units. */
const DESCRIPTION_WIDTH = 40;
/** Names the header as the container the rest of the header measures itself against. */
const HEADER_CONTAINER = "team-profile-header";

/** `theme.spacing` yields a CSS length; container-query thresholds have to add up as numbers. */
function spacingInPixels(theme: Theme, units: number): number {
  return parseFloat(theme.spacing(units));
}

function headerAtLeast(width: number): string {
  return `@container ${HEADER_CONTAINER} (min-width: ${width}px)`;
}

function headerNarrowerThan(width: number): string {
  return `@container ${HEADER_CONTAINER} (max-width: ${width - 0.05}px)`;
}

/** Below this the avatar, name, and actions stop sharing a row and center in a single column. */
function stackedHeaderWidth(theme: Theme): number {
  return theme.breakpoints.values.sm;
}

/**
 * The description earns a column beside the actions once the header can seat the avatar, two
 * actions, and a readable description at the same time. Anything narrower gives the description its
 * own full-width row, which reads far better than the sliver left over next to the buttons.
 */
function describedHeaderWidth(theme: Theme): number {
  return spacingInPixels(
    theme,
    AVATAR_WIDTH + HEADER_ACTION_WIDTH * 2 + DESCRIPTION_WIDTH + HEADER_GAP * 3
  );
}
// #endregion

// #region Styled Components
const MainContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "chatOpen",
})<{ chatOpen: boolean }>(({ theme, chatOpen }) => ({
  boxSizing: "border-box",
  display: "grid",
  gridTemplateRows: chatOpen ? "auto minmax(0, 1fr)" : "auto 1fr",
  height: chatOpen ? "min(75vh, calc(100vh - 160px))" : undefined,
  minHeight: chatOpen ? 0 : undefined,
  paddingBottom: chatOpen ? 0 : theme.spacing(8),
  width: "100%",
}));
const HeaderContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    padding: theme.spacing(3, 0),
  },
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  // The header lays itself out against its own width. The profile is opened in a dialog that is
  // narrower than the viewport, so viewport breakpoints would promise room the header does not have.
  containerName: HEADER_CONTAINER,
  containerType: "inline-size",
  display: "flex",
  flexDirection: "column",
  gridColumn: "1 / auto",
  gridRowStart: "1",
  justifyContent: "center",
  padding: theme.spacing(3),
  paddingTop: theme.spacing(1),
  position: "sticky",
  top: 0,
  width: "100%",
  zIndex: 10,
}));
const StyledAvatar = styled(TeamAvatar)(({ theme }) => ({
  [headerNarrowerThan(stackedHeaderWidth(theme))]: {
    height: theme.spacing(7),
    width: theme.spacing(7),
  },
  height: theme.spacing(AVATAR_WIDTH),
  width: theme.spacing(AVATAR_WIDTH),
}));
const ChatContainer = styled(Container)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    padding: 0,
  },
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(0, 1),
  },
  [theme.breakpoints.up("lg")]: {
    padding: theme.spacing(0, 4),
  },
  display: "flex",
  flexDirection: "column",
  gridColumn: "1 / auto",
  gridRowStart: "2",
  gridRowEnd: "none",
  height: "100%",
  minHeight: 0,
  overflow: "hidden",
}));
const HeaderInnerContainer = styled("div")(({ theme }) => ({
  [headerNarrowerThan(stackedHeaderWidth(theme))]: {
    justifyItems: "center",
  },
  [headerAtLeast(stackedHeaderWidth(theme))]: {
    gridTemplateColumns: "min-content auto",
    gridTemplateRows: "min-content auto",
  },
  // The description keeps a readable column of its own; the actions wrap onto a second row before
  // the description gives up any of it.
  [headerAtLeast(describedHeaderWidth(theme))]: {
    gridTemplateColumns: `min-content auto minmax(${theme.spacing(DESCRIPTION_WIDTH)}, 1fr)`,
  },
  alignItems: "flex-start",
  gap: theme.spacing(HEADER_GAP),
  display: "grid",
  width: "100%",
}));
const TitleContainer = styled("div")(({ theme }) => ({
  [headerNarrowerThan(stackedHeaderWidth(theme))]: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  display: "flex",
  flexDirection: "column",
  height: "100%",
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  [headerNarrowerThan(stackedHeaderWidth(theme))]: {
    gap: theme.spacing(1),
    justifyContent: "center",
    width: "100%",
    [HEADER_ACTIONS]: {
      flex: "1 1 100%",
      minWidth: 0,
    },
  },
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  gap: theme.spacing(HEADER_GAP),
  justifyContent: "flex-start",
  marginTop: theme.spacing(1),
  // The row owns the spacing and the width of every action, so that buttons carrying margins or a
  // minimum width of their own for other layouts cannot skew this one.
  [HEADER_ACTIONS]: {
    margin: 0,
    minWidth: theme.spacing(HEADER_ACTION_WIDTH),
  },
  "& > .MuiButtonGroup-root, & > span": {
    display: "inline-flex",
  },
  "& > span > .MuiButton-root": {
    margin: 0,
    width: "100%",
  },
}));
const ProfileTypeChip = styled(Chip)(({ theme }) => ({
  color: theme.palette.text.disabled,
  margin: theme.spacing(0.25),
}));
const NameChipContainer = styled("div")(({ theme }) => ({
  [headerNarrowerThan(stackedHeaderWidth(theme))]: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  alignItems: "flex-start",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  justifyContent: "flex-start",
}));
const DescriptionContainer = styled("div")(({ theme }) => ({
  [headerNarrowerThan(stackedHeaderWidth(theme))]: {
    alignItems: "center",
    justifyContent: "center",
    margin: theme.spacing(1, 0),
    width: "100%",
  },
  // Until the header is wide enough to seat a description beside the actions, the description takes
  // a full-width row of its own rather than a sliver of the row the actions are on.
  [headerAtLeast(stackedHeaderWidth(theme))]: {
    gridColumn: "1 / span 2",
    gridRow: "2",
    margin: theme.spacing(2, 0),
  },
  [headerAtLeast(describedHeaderWidth(theme))]: {
    gridColumn: "3",
    gridRow: "1",
    margin: 0,
  },
  display: "flex",
  flexDirection: "column",
  height: "100%",
  justifyContent: "space-between",
  margin: 0,
  textAlign: "left",
  width: "100%",
}));
const DescriptionText = styled(Typography)(({ theme }) => ({
  [headerNarrowerThan(stackedHeaderWidth(theme))]: {
    textAlign: "center",
  },
  fontSize: "1.1em",
  fontStyle: "italic",
  lineHeight: "1.4em",
  overflowWrap: "anywhere",
}));
const DescriptionDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(1),
  width: "100%",
}));
const MessageButtonContainer = styled("span")<
  { networked: boolean }>((
    { networked: inNetwork }) => ({
      cursor: inNetwork ? "pointer" : "not-allowed",
    }));
/** Keeps the team and its fee schedules stacked in one grid row, under the header. */
const ProfileBody = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
}));
/** Matches `ViewTeam`'s own padding, so the section lines up with the leader and member lists. */
const FeeSchedules = styled(TeamFeeSchedules)(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  paddingBottom: theme.spacing(3),
}));
// #endregion

// #region Component Input Props
export type ViewTeamProfileProps = {
  teamId: Guid;
  profile?: TeamProfile;
};

export default function ViewTeamProfile(props: Readonly<ViewTeamProfileProps>) {
  const { teamId } = props;

  // #region State
  const [chatOpen, setChatOpen] = useState<boolean>(false);

  // #region Hooks

  const navigate = useNavigate();
  const session = useSession();
  const teamQuery = useGetTeamByIdQuery({ teamId: teamId.value });

  // #region Selectors
  const team = useSelector(getTeamById(teamId));
  const { isLoading: isLoadingTeam, error: errorLoadingTeam } = teamQuery;
  const isNetworkedWithUser = useNetworkConnectionStatus(team?.profile.contactUserId) === true;
  const forumQuery = useGetChatForumForNetworkConnectionQuery(
    team?.profile?.contactUserId ? { userId: team?.profile.contactUserId } : skipToken
  );

  const forum = useMemo(() => {
    if (!forumQuery.data) return null;
    return Forum.fromJSON(forumQuery.data);
  }, [forumQuery.data]);

  // #region Lifecycle Hooks


  useEffect(() => {
    if (errorLoadingTeam) {
      enqueueSnackbar("Couldn't load team profile.", { variant: "warning" });
    }
  }, [errorLoadingTeam]);
  // #endregion

  function handleSendProposalToTeam(targetUrl: URL): void {
    navigate(targetUrl.pathname + targetUrl.search);
  }

  function renderProposalButtons() {
    if (!team?.id || !team.leader || team.leader.userId.isEqualTo(session.user?.id)) return null;

    const leaderName = Name.create({
      params: {
        firstName: team.leader.firstName,
        lastName: team.leader.lastName,
      },
    });

    return (
      <DirectProposalButton
        targetName={leaderName}
        targetTeamId={team.id}
        targetTeamName={team.profile.name}
        onClick={handleSendProposalToTeam}
        useStyledButton={true}
      />
    );
  }

  // #region Render
  return (
    <MainContainer chatOpen={chatOpen}>
      <HeaderContainer>
        <HeaderInnerContainer>
          <StyledAvatar
            team={team}
          />
          <TitleContainer>
            <NameChipContainer>
              <Typography variant="h4">{team?.profile?.name}</Typography>
              <span>
                {team?.profile?.categories?.map((category) => (
                  <ProfileTypeChip key={category} label={category} size="small" />
                ))}
              </span>
            </NameChipContainer>
            <ButtonContainer>
              {team?.profile?.contactUserId && <NetworkButton userId={team?.profile?.contactUserId} />}
              <Tooltip
                title={(function () {
                  if (chatOpen) return "Back to profile";
                  if (isNetworkedWithUser) return "Send message";
                  return "You must be in the user's network to send a message";
                })()}
              >
                <MessageButtonContainer networked={isNetworkedWithUser}>
                  <Button
                    variant="contained"
                    startIcon={chatOpen ? <BadgeIcon /> : <MessageIcon />}
                    disableElevation
                    disabled={!isNetworkedWithUser}
                    color="primary"
                    onClick={() => setChatOpen((prevValue) => !prevValue)}
                  >
                    {chatOpen ? "Back to profile" : "Message"}
                  </Button>
                </MessageButtonContainer>
              </Tooltip>
              {!chatOpen && renderProposalButtons()}
            </ButtonContainer>
          </TitleContainer>
          <DescriptionContainer>
            <DescriptionText variant="subtitle1">{team?.profile?.description}</DescriptionText>
            <DescriptionDivider />
          </DescriptionContainer>
        </HeaderInnerContainer>
      </HeaderContainer>
      <ChatContainer>
        {isLoadingTeam && <Loader />}
        {chatOpen && forumQuery && (
          <ChatWindow fillAvailableSpace forums={forum ? [forum] : []} />
        )}
      </ChatContainer>
      {!chatOpen && !isLoadingTeam && (
        <ProfileBody>
          <ViewTeam team={team} />
          <FeeSchedules teamId={teamId} />
        </ProfileBody>
      )}
    </MainContainer>
  );
}
// #endregion
